import React, { FC, HTMLAttributes, useMemo } from 'react';
import { FormApi, SubmissionErrors } from 'final-form';
import classNames from 'classnames';
import { Box } from 'components';
import { Heading, Note } from 'components/ui';
import { t } from 'utils';
import { RadiobuttonWrapper } from 'components/forms';
import { Field } from 'react-final-form';
import AppIconFontAwesome from './AppIconFontAwesome';
import AppIconDefault from './AppIconDefault';
import AppIconImage from './AppIconImage';

import styles from './AppIcon.module.scss';

export enum AppIconType {
  DEFAULT = 'DEFAULT',
  FONTAWESOME = 'FONTAWESOME',
  IMAGE = 'IMAGE',
}

export interface IAppIcon extends HTMLAttributes<HTMLDivElement> {
  form: FormApi;
  initialValues: { [key: string]: string };
  errors: SubmissionErrors;
  className?: string;
  disabled?: boolean;
  iconType: string;
  iconImageUrl: string;
  iconTextColor: string;
  iconBackgroundColor: string;
  iconFontAwesomeName: string;
  abbreviatedName: string;
}

export interface IRadioOption {
  label: string;
  name: string;
  value: string;
  hint: string;
  disabled?: boolean;
}

const AppIcon: FC<IAppIcon> = ({ className, disabled, iconType, form, initialValues, ...rest }) => {
  const { change } = form;
  const { iconImageUrl, iconTextColor, abbreviatedName, iconBackgroundColor, iconFontAwesomeName } =
    initialValues || {};
  const classes = classNames(styles['ta-app-icon'], className, { [styles.disabled]: !!disabled });
  const iconTypeOptions = useMemo(
    () => [
      {
        label: 'form.field.pricing.appIcon.default.label',
        hint: 'form.field.pricing.appIcon.default.hint',
        name: 'iconType',
        value: AppIconType.DEFAULT,
        onChangeAddOn: () => {
          change('iconFontAwesomeName', iconFontAwesomeName);
          change('iconBackgroundColor', iconBackgroundColor);
          change('iconTextColor', iconTextColor);
          change('iconImageUrl', iconImageUrl);
        },
        disabled,
      },
      {
        label: 'form.field.pricing.appIcon.image.label',
        hint: 'form.field.pricing.appIcon.image.hint',
        name: 'iconType',
        value: AppIconType.IMAGE,
        onChangeAddOn: () => {
          change('iconFontAwesomeName', iconFontAwesomeName);
          change('iconBackgroundColor', iconBackgroundColor);
          change('iconTextColor', iconTextColor);
          change('abbreviatedName', abbreviatedName);
        },
        disabled,
      },
      {
        label: 'form.field.pricing.appIcon.fontAwesome.label',
        hint: 'form.field.pricing.appIcon.fontAwesome.hint',
        name: 'iconType',
        value: AppIconType.FONTAWESOME,
        onChangeAddOn: () => {
          change('iconBackgroundColor', iconBackgroundColor);
          change('iconTextColor', iconTextColor);
          change('iconImageUrl', iconImageUrl);
          change('abbreviatedName', abbreviatedName);
        },
        disabled,
      },
    ],
    [
      change,
      disabled,
      iconImageUrl,
      iconTextColor,
      abbreviatedName,
      iconBackgroundColor,
      iconFontAwesomeName,
    ]
  );

  return (
    <div className={classes}>
      <Box halfPadding>
        <Heading noTopPadding>{t('app.icon.heading')}</Heading>
        {iconTypeOptions.map((field: IRadioOption, i: number) => (
          <React.Fragment key={i}>
            <Field {...field} type='radio' component={RadiobuttonWrapper} key={field.value} />
            <Note className={`mt-1 mb-${i === iconTypeOptions.length - 1 ? '4' : '2'}`}>
              {t(field.hint)}
            </Note>
          </React.Fragment>
        ))}
        <Box noOffset>
          <Heading noTopPadding className={styles['box-heading']}>
            {t(`app.icon.${iconType?.toLowerCase()}.box.heading`)}
          </Heading>
          {iconType === 'DEFAULT' && (
            <AppIconDefault {...{ disabled, iconType, form, initialValues, ...rest }} />
          )}
          {iconType === 'IMAGE' && (
            <AppIconImage {...{ disabled, iconType, form, initialValues, ...rest }} />
          )}
          {iconType === 'FONTAWESOME' && (
            <AppIconFontAwesome {...{ disabled, iconType, form, initialValues, ...rest }} />
          )}
        </Box>
      </Box>
    </div>
  );
};

export default AppIcon;
